import {Component, computed, inject, makeStateKey, signal, TransferState} from '@angular/core';
import {
  ActivatedRoute,
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet
} from '@angular/router';
import {FormsModule} from "@angular/forms";
import {ViewportScroller} from "@angular/common";
import {HeaderComponent} from "./components/header/header.component";
import {ConfirmDialog, ConfirmDialogModule} from "primeng/confirmdialog";
import {ConfirmationService} from "primeng/api";
import {ModalService} from "./services/modal.service";
import {distinctUntilChanged, filter, map, startWith, switchMap} from "rxjs";
import {getDeepestChildRoute} from "./utils";
import {Carousel} from "primeng/carousel";
import {Toast} from "primeng/toast";
import {ExternalCodeService} from "./services/external-code.service";
import {ProgressSpinner} from "primeng/progressspinner";
import {AnalyticsService} from "./services/analytics.service";
import {DrawerOutletComponent} from "./components/drawer-outlet/drawer-outlet.component";
import {DebugButtonComponent} from "./components/debug-button/debug-button.component";
import {NgClass} from "@angular/common";
import {TrackingPixelService} from "./services/tracking-pixel.service";
import {BackNavigationService} from "./services/back-navigation.service";
// call this early for PWAs so the plugin can pick up ?code=... on reload

const PRERENDER_KEY = makeStateKey<boolean>('PRERENDERED');

@Component({
  selector: 'homey-root',
  standalone: true,
  imports: [
    RouterOutlet,
    FormsModule,
    Toast,
    HeaderComponent,
    ConfirmDialogModule,
    ConfirmDialog,
    ProgressSpinner,
    DrawerOutletComponent,
    NgClass,
    DebugButtonComponent,
  ],
  providers: [
    ConfirmationService,
    ModalService
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  protected loading = false;
  private static readonly MAX_SCROLL_POSITIONS = 10;
  private scrollPositions = new Map<string, [number, number]>(); // Store recent scroll positions (bounded LRU)
  private previousUrl = '';
  private previousQueryParams = '';
  private readonly backNavigationService = inject(BackNavigationService);
  private readonly externalCodeService = inject(ExternalCodeService);
  private readonly hasDom = typeof window !== 'undefined' && typeof document !== 'undefined';

  protected readonly initialDataLoaded = signal(false);
  protected readonly wasPrerendered;

  private readonly analyticsService = inject(AnalyticsService);
  private readonly trackingPixelService = inject(TrackingPixelService);
  protected readonly backgroundType = signal<'default' | 'blue-gradient'>('default');

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly viewportScroller: ViewportScroller,
    private readonly transferState: TransferState
  ) {
    this.trackingPixelService.init();
    this.wasPrerendered = this.transferState.get(PRERENDER_KEY, false);

    this.router.events.pipe(
      switchMap(() => getDeepestChildRoute(this.activatedRoute).queryParamMap),
    ).subscribe((queryParams) => {
      this.externalCodeService.updateQueryParams(queryParams);
    });

    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.initialDataLoaded.set(true);
      }
    });

    // watch deepest route data for background preference
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => getDeepestChildRoute(this.activatedRoute)),
      map(route => route.snapshot.data?.['backgroundType'] as ('default' | 'blue-gradient' | undefined)),
      startWith('default'),
      distinctUntilChanged(),
    ).subscribe(type => {
      this.backgroundType.set(type === 'blue-gradient' ? 'blue-gradient' : 'default');
    });

    Carousel.prototype.onTouchMove = () => {};

    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        const currentUrl = this.router.url.split('?')[0]; // Remove query params
        const currentQueryParams = this.router.url.includes('?') ? this.router.url.split('?')[1] : '';
        const urlTree = this.router.parseUrl(this.router.url);
        const fragment = urlTree.fragment;

        // If only query params changed, do not scroll
        if (this.previousUrl === currentUrl && this.previousQueryParams !== currentQueryParams) {
          this.previousQueryParams = currentQueryParams;
          return;
        }

        // Store previous scroll position before navigating (most-recently-used)
        if (this.previousUrl) {
          this.scrollPositions.delete(this.previousUrl);
          this.scrollPositions.set(this.previousUrl, this.viewportScroller.getScrollPosition());
          while (this.scrollPositions.size > AppComponent.MAX_SCROLL_POSITIONS) {
            const oldestUrl = this.scrollPositions.keys().next().value;
            if (oldestUrl === undefined) break;
            this.scrollPositions.delete(oldestUrl);
          }
        }

        if (!this.hasDom) {
          this.previousUrl = currentUrl;
          this.previousQueryParams = currentQueryParams;
          return;
        }

        // If navigating back, restore scroll position
        if (fragment) {
          const headerBuffer = 250; // Adjust this value to your header height
          setTimeout(() => {
            const element = document.getElementById(fragment);
            if (element) {
              const elementRect = element.getBoundingClientRect();
              const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
              const elementPosition = elementRect.top + scrollTop;
              const offsetPosition = elementPosition - headerBuffer;
              window.scrollTo({top: offsetPosition, behavior: 'smooth'});
            }
          }, 0);
        } else if (this.backNavigationService.lastNavigationWasBack() && this.scrollPositions.has(currentUrl)) {
          const savedPosition = this.scrollPositions.get(currentUrl) || [0, 0];
          // Refresh recency so frequently-revisited pages aren't evicted
          this.scrollPositions.delete(currentUrl);
          this.scrollPositions.set(currentUrl, savedPosition);
          setTimeout(() => this.viewportScroller.scrollToPosition(savedPosition), 0);
        } else {
          // Otherwise, scroll to the top
          setTimeout(() => this.viewportScroller.scrollToPosition([0, 0]), 0);
        }

        // Update previous values
        this.previousUrl = currentUrl;
        this.previousQueryParams = currentQueryParams;
      }
    });
    this.router.events.subscribe((e) => {
      this.navigationInterceptor(e);
    })
  }

  // Shows and hides the loading spinner during RouterEvent changes
  navigationInterceptor(event: Event): void {
    if (event instanceof NavigationStart) {
      this.loading = true
    }
    if (event instanceof NavigationEnd) {
      this.loading = false
    }

    // Set loading state to false in both of the below events to hide the spinner in case a request fails
    if (event instanceof NavigationCancel) {
      this.loading = false
    }
    if (event instanceof NavigationError) {
      this.loading = false
    }
  }

  protected mainBgClass = computed(() => {
    return this.backgroundType() === 'blue-gradient' ? 'app-bg-blue-gradient' : 'app-bg-default';
  });

  protected innerClass = computed(()=> {
    return this.backgroundType() === 'blue-gradient'
      ? 'app-inner--transparent'
      : 'app-inner--default';
  });
}
